/**
 * The platform's own default chords (架构方案 §5.9).
 *
 * Declared in the same shape a module contributes so they flow through one
 * merge, one conflict check and one settings page — the built-ins are not a
 * privileged second table, they are just the lowest-precedence source.
 */

import type { ResolvedKeybinding } from '@hibro/shared';
import { keybindingId, MAIN_SHORTCUT_COMMAND } from '@hibro/shared';

import {
  BUILTIN_COMMAND,
  PLATFORM_MODULE_ID,
} from '../commands/builtin-commands';

type BuiltinDeclaration = Omit<
  ResolvedKeybinding,
  'moduleId' | 'id' | 'source'
>;

export interface BuiltinKeybindingOptions {
  /** A development build; gates the reload chords. */
  dev: boolean;
  mac: boolean;
}

/**
 * The built-in table for this build and platform.
 *
 * A function rather than a constant because two of these must not *exist*
 * outside their context, and a `when` clause cannot express that: the main
 * process only installs the reload chords in a dev build, so publishing them
 * from a packaged renderer would hand it chords it then executes unconditionally
 * — `when` is evaluated at dispatch, and main never sees it.
 */
export function builtinKeybindings({
  dev,
  mac,
}: BuiltinKeybindingOptions): readonly ResolvedKeybinding[] {
  const declarations: BuiltinDeclaration[] = [
    // The VS Code split, and for its reason: the shifted chord means "commands"
    // (it opens the box already in `/` mode), the plain one means "search
    // anything" (recent, apps, module content).
    { command: BUILTIN_COMMAND.commandPalette, key: 'mod+shift+p' },
    { command: BUILTIN_COMMAND.search, key: 'mod+p' },
    { command: BUILTIN_COMMAND.openSettings, key: 'mod+,' },
    {
      command: BUILTIN_COMMAND.captureRegion,
      key: 'mod+shift+a',
      scope: 'global',
      runtimes: ['desktop'],
      args: [{ purpose: 'clipboard' }],
    },
  ];
  if (!mac) {
    // macOS has a system-wide full-screen chord and spends F11 on Mission
    // Control, so claiming it there fights the OS rather than serving the user.
    declarations.push({
      command: MAIN_SHORTCUT_COMMAND.toggleFullScreen,
      key: 'f11',
      runtimes: ['desktop'],
    });
  }
  if (dev) {
    // `Shift`-modified rather than bare F5 / Ctrl+R (见 01): these are swallowed
    // before a module's own binding can see them, and F5 ("run") is exactly the
    // key a module is most likely to want.
    declarations.push(
      {
        command: MAIN_SHORTCUT_COMMAND.reloadWindow,
        key: 'mod+shift+r',
        runtimes: ['desktop'],
      },
      {
        command: MAIN_SHORTCUT_COMMAND.reloadWindowHard,
        key: 'mod+shift+f5',
        runtimes: ['desktop'],
      },
    );
  }
  return declarations.map((declaration) => ({
    ...declaration,
    moduleId: PLATFORM_MODULE_ID,
    id: keybindingId({ ...declaration, moduleId: PLATFORM_MODULE_ID }),
    source: 'builtin',
  }));
}
